import { createLogger } from '@/lib/log/client'
import { stripStoriesPrefix } from '@/lib/utils/strip-stories-prefix'

const log = createLogger('normalize-url')

// Hosts that ARE this site. Absolute links to these (Storyblok content often
// bakes in the full domain) get rewritten to relative paths so they stay in-app
// for SPA navigation and don't pin a domain — which is what lets a QA/preview
// env work. Kept domain-agnostic on purpose: the list is config, never
// hardcoded in this logic. NEXT_PUBLIC_SITE_HOSTS is comma-separated; the
// canonical prod hosts must stay listed even on QA because the content
// references them. Unset => no rewriting (safe no-op, original behavior).
const internalHosts = (): string[] =>
  (process.env.NEXT_PUBLIC_SITE_HOSTS || '')
    .split(',')
    .map((h) => h.trim().toLowerCase())
    .filter(Boolean)

// A path ending in a file extension (e.g. /assets/icon.png) is an asset, not a
// page — leave those absolute, since the file may only exist on the origin host.
const looksLikeFile = (pathname: string) => /\.[a-z0-9]{2,5}$/i.test(pathname)

// If `full` is an absolute URL pointing at one of our own hosts, return just its
// path (relative); otherwise return it untouched (external links stay absolute).
const internalize = (full: string): string => {
  const hosts = internalHosts()
  if (hosts.length === 0) return full
  try {
    const parsed = new URL(full)
    if (hosts.includes(parsed.host.toLowerCase()) && !looksLikeFile(parsed.pathname)) {
      return `${parsed.pathname}${parsed.search}${parsed.hash}`
    }
  } catch {
    // not a parseable absolute URL — leave as-is
  }
  return full
}

export const normalizeUrl = ({ url, origin }: { url: string; origin: string }) => {
  if (!url || typeof url !== 'string') {
    log.warn('[%s]: normalizeUrl passed invalid url [%o]', origin, url)
    return ''
  }
  if (url.startsWith('https')) {
    return internalize(url)
  }
  if (url.startsWith('http:')) {
    return internalize(url.replace('http:', 'https:'))
  }
  if (url.startsWith('//')) {
    // probably the storyblok ones that start with //, or maybe an old http one
    return internalize(`https:${url}`)
  }
  if (url.startsWith('/')) {
    // absolute path — strip the Storyblok `/stories/` folder prefix if a
    // multilink baked it in, so it never leaks to the public site.
    return stripStoriesPrefix(url)
  }
  // these are probably story slugs, e.g. best-hearing-aids
  if (url.startsWith('stories/')) {
    return url.replace(/^stories/, '')
  }
  if (/^([a-zA-Z0-9-]+\.)+[a-zA-Z]{2,}$/.test(url.split('/')[0])) {
    return internalize(`https://${url}`)
  }
  return `/${url}`
}
